import { ClockIcon, KeyIcon } from '@heroicons/react/24/outline'
import { format, parseISO } from 'libs/dates'
import { useForm } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { Link, useParams } from 'libs/router'
import { BackButton, Form, Header, HistoryButton, Panel } from 'shared/components'
import { useFetchCountries } from 'shared/queries/useFetchCountries'
import { routes } from 'config/routes'
import { useEditUserValidationSchema } from './hooks/useEditUserValidationSchema'
import { usePopulateUser } from './hooks/usePopulateUser'
import { useSetCountry } from './hooks/useSetCountry'
import { useEditUserMutation } from './mutations/useEditUserMutation'
import { useFetchUser } from './queries/useFetchUser'
import { UserFormSections } from './organisms/UserForm/UserFormSections'
import { AccessControl } from './organisms/AccessControl/AccessControl'
import { UserBookings } from './organisms/Bookings/Bookings'
import { UserSales } from './organisms/Sales/Sales'
import { UserProducts } from './organisms/Products/Products'

const securityLinkClassName =
	'inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground shadow transition-colors hover:bg-muted'

const formatLogin = (value?: string) => (value ? format(parseISO(value), 'dd/MM/yyyy, HH:mm') : '—')

const UserPage = () => {
	const { t } = useTranslation()
	const params = useParams()
	const userId = params.id as string
	const validationResolver = useEditUserValidationSchema()
	const { countries, isCountriesLoading } = useFetchCountries()
	const form = useForm({
		mode: 'onChange',
		resolver: validationResolver
	})
	const { handleSubmit, setValue, setError, reset } = form
	const { editUserMutation, isEditUserLoading } = useEditUserMutation(setError)
	const { user } = useFetchUser()

	usePopulateUser({
		data: user,
		reset
	})

	useSetCountry({
		countries,
		country: user?.address.country,
		setValue
	})

	const onSubmit = (data: any) => {
		const formattedData = {
			...data,
			address: {
				...data.address,
				country: data.country.value
			}
		}

		editUserMutation(formattedData)
	}

	const activity = [
		{ label: t('First login'), value: user?.date_joined },
		{ label: t('Last login'), value: user?.last_login }
	]

	return (
		<>
			<Header
				title={user?.name || user?.email || t('User')}
				actions={
					<>
						<BackButton to={routes.userIndex} text={t('Back to users')} />
						<HistoryButton to={routes.userHistory(userId)} />
					</>
				}
			/>
			<div className="mx-auto grid max-w-[1400px] gap-5 px-5 lg:px-0 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
				<Form
					variant="sections"
					className="max-w-none"
					submitText={t('Save changes')}
					onSubmit={handleSubmit(onSubmit)}
					isLoading={isEditUserLoading}>
					<UserFormSections
						form={form}
						countries={countries}
						isCountriesLoading={isCountriesLoading}
					/>
				</Form>
				<aside className="space-y-5">
					<AccessControl form={form} />
					<Panel title={t('Activity')} icon={<ClockIcon />}>
						<dl className="space-y-2.5 text-[12px]">
							{activity.map(({ label, value }) => (
								<div key={label} className="flex justify-between gap-3">
									<dt className="text-muted-foreground">{label}</dt>
									<dd className="font-medium tabular-nums text-foreground">{formatLogin(value)}</dd>
								</div>
							))}
						</dl>
					</Panel>
					<Panel title={t('Security')} icon={<KeyIcon />}>
						<div className="grid grid-cols-2 gap-2">
							<Link className={securityLinkClassName} to="/password-reset">
								{t('Reset password')}
							</Link>
							<Link className={securityLinkClassName} to={routes.userChangePassword(userId)}>
								{t('Change password')}
							</Link>
						</div>
					</Panel>
					<UserBookings userId={userId} />
					<UserSales userId={userId} />
					<UserProducts userId={userId} />
				</aside>
			</div>
		</>
	)
}

export default UserPage
